/*
 * Fix Alignment — Office.js geometry bridge.
 *
 * Reads the slide's shape geometry into the shape the /alignment/fix payload
 * needs, and writes the returned corrections back onto the live shapes.
 *
 * Loads are phased (collection → geometry → textFrame → text/font) because a
 * single invalid property load poisons the whole `context.sync()` batch. The
 * geometry phase is required; the text phases are best-effort — text and font
 * size only sharpen the AI's overflow detection, so losing them still yields a
 * usable request rather than a dead end. This mirrors the guarded-load approach
 * already used by brandChecker.ts.
 */

import { EMU_PER_POINT, SLIDE_HEIGHT, SLIDE_WIDTH } from "../../../const";
import { hasPpt, ppt } from "../../../utils/formatUtils";
import { formatUnknown, getErrorMessage, logFixAlignmentError } from "./fixAlignmentApi";

/** Which shapes the request covers — mirrors AlignmentRequest.scope. */
export type AlignmentScope = "selected" | "all";

/** Shape types that never carry a text frame — skip textFrame loading for these. */
const NO_TEXTFRAME_TYPES = new Set(
  [
    "image", "picture", "linkedImage", "line", "connector", "contentApp",
    "media", "model3D", "ole", "diagram", "smartArt", "graphic", "ink",
    "unsupported", "table", "chart", "group",
  ].map((t) => t.toLowerCase()),
);

/** Shape types PowerPoint reports for pictures — used for the aspect-lock hint. */
const IMAGE_TYPES = new Set(["image", "picture", "linkedimage"]);

/** Office.js property lists, named so the string literals aren't repeated. */
const ITEMS = "items";
const GEOMETRY_FIELDS = "id,name,type,left,top,width,height";
const PAGE_SETUP_FIELDS = "slideWidth,slideHeight";

export interface SlideShapeSnapshot {
  readonly id: string;
  readonly name: string;
  readonly type: string;
  readonly text: string;
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly hasText: boolean;
  readonly fontSize: number | null;
  readonly isImage: boolean;
  readonly aspectLocked: boolean;
  readonly aspectRatio: number | null;
}

export interface SlideSnapshot {
  readonly slide: { readonly widthPts: number; readonly heightPts: number };
  readonly shapes: readonly SlideShapeSnapshot[];
}

/** User-facing copy, centralized so the hook and this module never drift. */
export const PPT_ONLY_MESSAGE = "This tool works only in PowerPoint.";
export const NO_SLIDE_MESSAGE = "Open a slide first.";
export const NO_SELECTION_MESSAGE = "Select at least two shapes, or switch to the whole slide.";
export const NO_SHAPES_MESSAGE = "This slide has no shapes to align.";

/** Fix Alignment needs a relationship between shapes to have anything to fix. */
const MIN_SHAPES = 2;

/**
 * Above this, a pageSetup dimension can only be EMUs — the largest slide
 * PowerPoint allows is 56in = 4032pt, so anything this big is not points.
 */
const EMU_DETECTION_THRESHOLD_PTS = 10_000;

/** ShapeGeometry.fontSize's accepted range in the API schema — outside it, 422. */
const MIN_REPORTED_FONT_SIZE = 1;
const MAX_REPORTED_FONT_SIZE = 400;

function isFinitePositive(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

/**
 * PowerPoint reports geometry in points, but `pageSetup.slideWidth/Height` is
 * EMUs on some hosts and points on others. Convert the EMU case; anything
 * smaller is already points. Falls back to the widescreen default when
 * pageSetup is unavailable or reports a nonsense value.
 */
function normalizeSlideDimension(raw: number | undefined, fallback: number): number {
  if (!isFinitePositive(raw)) return fallback;
  return raw > EMU_DETECTION_THRESHOLD_PTS ? raw / EMU_PER_POINT : raw;
}

/** Sync a queued batch, reporting (but not rethrowing) a poisoned best-effort phase. */
async function trySync(context: PowerPoint.RequestContext, reason: string): Promise<boolean> {
  try {
    await context.sync();
    return true;
  } catch (error) {
    logFixAlignmentError(reason, getErrorMessage(error, "sync failed"));
    return false;
  }
}

/**
 * Collects the reasons individual property loads were skipped during one pass.
 *
 * Office.js throws per-shape for properties a given shape type doesn't support,
 * which is expected and must not fail the whole read — but the failures are
 * still recorded and reported once as a summary (rather than swallowed, or
 * spammed one telemetry event per shape on a 200-shape slide).
 */
class SkippedLoadLog {
  private readonly reasons = new Map<string, number>();

  record(error: unknown): void {
    // Office.js throws OfficeExtension.Error, which is not an `Error`
    // instance — formatUnknown keeps those readable in the log.
    const key = error instanceof Error ? error.message : formatUnknown(error);
    this.reasons.set(key, (this.reasons.get(key) ?? 0) + 1);
  }

  /** Emit one aggregated event, if anything was skipped. */
  flush(reason: string): void {
    if (this.reasons.size === 0) return;
    const summary = [...this.reasons.entries()]
      .map(([message, count]) => `${message} (x${count})`)
      .join("; ");
    logFixAlignmentError(reason, `Skipped optional shape properties: ${summary}`);
    this.reasons.clear();
  }
}

/** Run `queue` guarded — an unsupported property must not abort the whole read. */
function tryQueue(queue: () => void, log?: SkippedLoadLog): boolean {
  try {
    queue();
    return true;
  } catch (error) {
    log?.record(error);
    return false;
  }
}

function tryGet<T>(read: () => T, log?: SkippedLoadLog): T | undefined {
  try {
    return read();
  } catch (error) {
    log?.record(error);
    return undefined;
  }
}

interface ShapeEntry {
  shape: PowerPoint.Shape;
  type: string;
  wantText: boolean;
  hasText: boolean;
}

/** Resolve the shape collection for the requested scope (already `load`ed). */
async function loadScopedShapes(
  context: PowerPoint.RequestContext,
  scope: AlignmentScope,
): Promise<PowerPoint.Shape[]> {
  if (scope === "selected") {
    const selected = context.presentation.getSelectedShapes();
    selected.load(ITEMS);
    await context.sync();
    const items = selected.items ?? [];
    if (items.length < MIN_SHAPES) throw new Error(NO_SELECTION_MESSAGE);
    return items;
  }

  const slides = context.presentation.getSelectedSlides();
  slides.load(ITEMS);
  await context.sync();
  const slide = slides.items?.[0];
  if (!slide) throw new Error(NO_SLIDE_MESSAGE);

  slide.shapes.load(ITEMS);
  await context.sync();
  const items = slide.shapes.items ?? [];
  if (items.length === 0) throw new Error(NO_SHAPES_MESSAGE);
  return items;
}

async function readSlideSize(
  context: PowerPoint.RequestContext,
): Promise<{ widthPts: number; heightPts: number }> {
  const pageSetup = context.presentation.pageSetup;
  if (!tryQueue(() => pageSetup.load(PAGE_SETUP_FIELDS))) {
    return { widthPts: SLIDE_WIDTH, heightPts: SLIDE_HEIGHT };
  }
  if (!(await trySync(context, "fix-alignment-page-setup"))) {
    return { widthPts: SLIDE_WIDTH, heightPts: SLIDE_HEIGHT };
  }
  return {
    widthPts: normalizeSlideDimension(tryGet(() => pageSetup.slideWidth), SLIDE_WIDTH),
    heightPts: normalizeSlideDimension(tryGet(() => pageSetup.slideHeight), SLIDE_HEIGHT),
  };
}

/** Phase 3+4: best-effort text + font size, so the AI can spot overflow. */
async function loadTextDetails(
  context: PowerPoint.RequestContext,
  entries: ShapeEntry[],
  log: SkippedLoadLog,
): Promise<void> {
  const textCapable = entries.filter((e) => e.wantText);
  if (textCapable.length === 0) return;

  for (const entry of textCapable) {
    entry.wantText = tryQueue(() => entry.shape.textFrame.load("hasText"), log);
  }
  if (!(await trySync(context, "fix-alignment-text-frames"))) return;

  const withText: ShapeEntry[] = [];
  for (const entry of textCapable) {
    if (!entry.wantText) continue;
    entry.hasText = tryGet(() => entry.shape.textFrame.hasText, log) ?? false;
    if (!entry.hasText) continue;
    const queued =
      tryQueue(() => entry.shape.textFrame.textRange.load("text"), log) &&
      tryQueue(() => entry.shape.textFrame.textRange.font.load("size"), log);
    if (queued) withText.push(entry);
  }
  if (withText.length > 0) await trySync(context, "fix-alignment-text-content");
}

/**
 * Font size must satisfy ShapeGeometry.fontSize's range or the request is 422'd;
 * anything outside it is reported as "unknown" instead.
 */
function readFontSize(entry: ShapeEntry, log: SkippedLoadLog): number | null {
  if (!entry.hasText) return null;
  const size = tryGet(() => entry.shape.textFrame.textRange.font.size, log);
  if (typeof size !== "number" || !Number.isFinite(size)) return null;
  if (size < MIN_REPORTED_FONT_SIZE || size > MAX_REPORTED_FONT_SIZE) return null;
  return size;
}

/**
 * Shapes the API can't accept: a blank id (it can't be corrected back), a
 * non-positive or non-finite size, or a duplicate id (rejected as 422). These
 * are dropped from the payload, which leaves them untouched on the slide.
 */
function toSnapshot(entries: ShapeEntry[], log: SkippedLoadLog): SlideShapeSnapshot[] {
  const snapshots: SlideShapeSnapshot[] = [];
  const seenIds = new Set<string>();

  for (const entry of entries) {
    const { shape } = entry;
    const id = String(shape.id ?? "");
    if (!id || seenIds.has(id)) continue;
    const { left, top, width, height } = shape;
    if (!Number.isFinite(left) || !Number.isFinite(top)) continue;
    if (!isFinitePositive(width) || !isFinitePositive(height)) continue;
    seenIds.add(id);

    const isImage = IMAGE_TYPES.has(entry.type);
    snapshots.push({
      id,
      name: String(shape.name ?? ""),
      type: String(shape.type ?? ""),
      text: entry.hasText ? (tryGet(() => shape.textFrame.textRange.text, log) ?? "") : "",
      left,
      top,
      width,
      height,
      hasText: entry.hasText,
      fontSize: readFontSize(entry, log),
      isImage,
      // Office.js exposes no lockAspectRatio, so pictures are reported as
      // aspect-locked: that makes the API reject a correction that would
      // stretch them rather than let it through and distort the deck.
      aspectLocked: isImage,
      aspectRatio: width / height,
    });
  }
  return snapshots;
}

/** Read the slide's geometry for the requested scope. */
export function readSlideGeometry(scope: AlignmentScope): Promise<SlideSnapshot> {
  if (!hasPpt()) return Promise.reject(new Error(PPT_ONLY_MESSAGE));

  return ppt().run(async (context) => {
    const shapes = await loadScopedShapes(context, scope);
    const slide = await readSlideSize(context);
    const log = new SkippedLoadLog();

    for (const shape of shapes) shape.load(GEOMETRY_FIELDS);
    await context.sync();

    const entries: ShapeEntry[] = shapes.map((shape) => {
      const type = String(shape.type ?? "").toLowerCase();
      return { shape, type, wantText: !NO_TEXTFRAME_TYPES.has(type), hasText: false };
    });
    await loadTextDetails(context, entries, log);

    const snapshots = toSnapshot(entries, log);
    log.flush("fix-alignment-read-geometry");
    if (snapshots.length < (scope === "selected" ? MIN_SHAPES : 1)) {
      throw new Error(scope === "selected" ? NO_SELECTION_MESSAGE : NO_SHAPES_MESSAGE);
    }
    return { slide, shapes: snapshots };
  });
}

/** One shape's target geometry — a correction, or an original for the revert. */
export interface GeometryUpdate {
  readonly id: string;
  readonly left?: number;
  readonly top?: number;
  readonly width?: number;
  readonly height?: number;
  readonly fontSize?: number;
}

/**
 * Write one shape's geometry, skipping any value that isn't safe to assign.
 *
 * The API validates its own corrections, so this is defence in depth against a
 * malformed or intercepted response: writing NaN or a non-positive size to a
 * live shape would visibly corrupt the user's deck.
 */
function applyUpdate(shape: PowerPoint.Shape, update: GeometryUpdate, log: SkippedLoadLog): boolean {
  let changed = false;
  const assign = (condition: boolean, write: () => void): void => {
    if (condition && tryQueue(write, log)) changed = true;
  };

  assign(Number.isFinite(update.left), () => { shape.left = update.left!; });
  assign(Number.isFinite(update.top), () => { shape.top = update.top!; });
  assign(isFinitePositive(update.width), () => { shape.width = update.width!; });
  assign(isFinitePositive(update.height), () => { shape.height = update.height!; });
  assign(isFinitePositive(update.fontSize), () => {
    shape.textFrame.textRange.font.size = update.fontSize!;
  });
  return changed;
}

/**
 * Write geometry updates back onto the live shapes, matched by id, and return
 * how many shapes actually changed. Ids missing from the current scope are
 * skipped — the user may have changed the selection while the call was in
 * flight, and a stale id must not fail the whole apply.
 */
export function applyGeometryUpdates(
  scope: AlignmentScope,
  updates: readonly GeometryUpdate[],
): Promise<number> {
  if (!hasPpt()) return Promise.reject(new Error(PPT_ONLY_MESSAGE));
  if (updates.length === 0) return Promise.resolve(0);

  return ppt().run(async (context) => {
    const shapes = await loadScopedShapes(context, scope);
    const log = new SkippedLoadLog();
    for (const shape of shapes) shape.load("id");
    await context.sync();

    const byId = new Map<string, PowerPoint.Shape>();
    for (const shape of shapes) {
      const id = String(shape.id ?? "");
      if (id && !byId.has(id)) byId.set(id, shape);
    }

    let changed = 0;
    for (const update of updates) {
      const shape = byId.get(update.id);
      if (shape && applyUpdate(shape, update, log)) changed += 1;
    }
    await context.sync();
    log.flush("fix-alignment-apply-geometry");
    return changed;
  });
}

/** The original geometry of every shape a correction touched, for the revert. */
export function toRevertUpdates(
  snapshot: SlideSnapshot,
  correctedIds: readonly string[],
): GeometryUpdate[] {
  const wanted = new Set(correctedIds);
  return snapshot.shapes
    .filter((shape) => wanted.has(shape.id))
    .map((shape) => ({
      id: shape.id,
      left: shape.left,
      top: shape.top,
      width: shape.width,
      height: shape.height,
      ...(shape.fontSize !== null ? { fontSize: shape.fontSize } : {}),
    }));
}
